import { roundUpTo, roundWithLocale } from '@sketch/utils'
import {
  isTextLayerStyle,
  formatCssRgbaColor,
  Border,
  SketchFrameElement,
  SketchLayerElement,
  SketchLayerCSSProperties,
  Gradient,
  TextLayerStyleProperties,
  MainAxisAlignment,
  CrossAxisAlignment,
  StackPadding,
} from '../../../../inspector'
import { gradientAngle } from './components/GradientAttribute/math'

export type CSSStyle = TextLayerStyleProperties & {
  corners?: {
    cornerRadius?: number[]
  }
}

export type InnerPropertyStyle =
  | { innerProperty: PropertyStyle; value?: never; color?: never }
  | { innerProperty?: never; value?: string; color?: string }

interface PropertyStyle {
  property?: string
  values: InnerPropertyStyle[]
}

export type CSStyles = PropertyStyle[]

interface GenerateLayerCSSValue {
  css: CSStyles
  toString: () => string
}

/**
 * We used to get this value from Sketch as "font.originalValue" but the new presentation
 * file format does not contain it anymore. So we need to compute it in the FE.
 * We don't think this is a problem because the font family name used in the code
 * is unlikely to match the font family name used in the design anyway.
 */
function getComputedCssFontFamilyName(
  fontFamily: string | undefined,
  fontStyle: string | undefined
) {
  const computedFamilyNameParts = []

  const removeSpaces = (str: string) => str.replace(/\s/g, '')

  if (fontFamily) {
    computedFamilyNameParts.push(removeSpaces(fontFamily))
  }

  if (fontStyle) {
    computedFamilyNameParts.push(removeSpaces(fontStyle))
  }

  return computedFamilyNameParts.join('-')
}

const formatPixelValue = (value: number | string) =>
  value === 0 || value === '0' ? value : `${value}px`

const roundAndFormatPixelValue = (value: number) =>
  formatPixelValue(roundUpTo(value, 2).toString()) || ''

const getCornerCSS = (
  element: SketchLayerCSSProperties
): PropertyStyle | undefined => {
  if (!element?.corners) {
    return
  }

  if (element.corners.radius) {
    const radius = element.corners.radius
    const hasRadius = radius.some(n => n !== 0)

    if (!hasRadius) {
      return
    }

    const normalizedRadius = radius.every(r => r === radius[0])
      ? roundAndFormatPixelValue(radius[0])
      : `${radius.join('px ')}px`

    return { property: 'border-radius', values: [{ value: normalizedRadius }] }
  }
}

const getBorderColor = (border: Border) => {
  if (border.color) {
    return formatCssRgbaColor(border.color)
  } else if (border.gradient) {
    // We don't really handle borders with gradient yet.
    // The old inspector data was getting a color property when there was a gradient
    // and that was what was shown. Since we don't have this prop anymore,
    // we replicate that behavior by extracting the last color of the gradient.
    const gradientStops = border.gradient.stops
    const lastGradientColor = gradientStops[gradientStops.length - 1]
    return formatCssRgbaColor(lastGradientColor.color)
  }
}

const getBorderStyle = (style: CSSStyle) => {
  let borderStyle = 'solid'

  // TODO: Fix dashed border
  // https://linear.app/sketch/issue/SWEB-938

  // if only the dash ([0]) is defined but not the gap ([1]), then it's a solid border
  if (
    style.borderOptions?.dashPattern?.length &&
    style.borderOptions?.dashPattern[1] !== 0
  ) {
    // so dotted borders are the ones that have a dash of only 1px, otherwise it's a dashed border
    borderStyle = style.borderOptions.dashPattern[0] === 1 ? 'dotted' : 'dashed'
  }

  return borderStyle
}

const getBorderCSS = (style: CSSStyle): PropertyStyle | undefined => {
  // ignore borders if this is a text-style
  if (style.text || !style.borders || !style?.borders?.length) return

  // Only one border, exported as CSS border property
  if (style.borders.length === 1) {
    const border = style.borders[0]

    if (!border.isEnabled) {
      return
    }

    const borderStyle = getBorderStyle(style)
    return {
      property: 'border',
      values: [
        {
          value: `${roundAndFormatPixelValue(border.thickness)} ${borderStyle}`,
          color: getBorderColor(border),
        },
      ],
    }
  }

  const borderValues: InnerPropertyStyle[] = []

  // Multiple borders, are exported as box-shadow
  style.borders.forEach(border => {
    if (!border.isEnabled) {
      return
    }

    // "thickness" needs to be divided by half, otherwise borders will not
    // overlap and will render next to each other
    const thickness = border.thickness / 2
    const position = border.position === 'inside' ? 'inset' : undefined

    const commonValues = {
      value: `0 0 0 ${roundAndFormatPixelValue(thickness)}`,
      color: getBorderColor(border),
    }

    borderValues.push(commonValues)

    const positionStr = position ? ` ${position}` : ''
    borderValues.push({
      ...commonValues,
      // For this second border, the position needs to be the opposite of the
      // first one, otherwise will overlap the first one
      value: `0 0 0 ${roundAndFormatPixelValue(thickness)}${positionStr}`,
    })
  })

  return {
    property: 'box-shadow',
    // It's reversed to match the Mac app order
    values: borderValues.reverse(),
  }
}

const getShadowsCSS = (style: CSSStyle): PropertyStyle => {
  const innerShadows: InnerPropertyStyle[] = []
  const outerShadows: InnerPropertyStyle[] = []

  style.innerShadows?.forEach(
    ({ isEnabled, offsetX, offsetY, blurRadius, spread, color }) => {
      if (!isEnabled) {
        return
      }

      const rgba = formatCssRgbaColor(color)

      // order of the parameters is: x y blur spread color
      const formattedOffsetX = roundAndFormatPixelValue(offsetX)
      const formattedOffsetY = roundAndFormatPixelValue(offsetY)
      const formattedBlurRadius = roundAndFormatPixelValue(blurRadius)
      const formattedSpread = roundAndFormatPixelValue(spread || 0)
      innerShadows.push({
        value: `${formattedOffsetX} ${formattedOffsetY} ${formattedBlurRadius} ${formattedSpread} inset`,
        color: rgba,
        // position: 'inset',
      })
    }
  )

  style.shadows?.forEach(
    ({ isEnabled, offsetX, offsetY, blurRadius, spread, color }) => {
      if (!isEnabled) {
        return
      }

      const rgba = formatCssRgbaColor(color)

      // order of the parameters is: x y blur spread color
      const formattedOffsetX = roundAndFormatPixelValue(offsetX)
      const formattedOffsetY = roundAndFormatPixelValue(offsetY)
      const formattedBlurRadius = roundAndFormatPixelValue(blurRadius)
      const formattedSpread = roundAndFormatPixelValue(spread || 0)
      outerShadows.push({
        value: `${formattedOffsetX} ${formattedOffsetY} ${formattedBlurRadius} ${formattedSpread}`,
        color: rgba,
      })
    }
  )

  /**
   * TODO: Review text shadows, this is not working
   * https://linear.app/sketch/issue/SWEB-996
   */
  // if (style.text && shadows.length) {
  //   // text shadows instead of box
  //   css.push({ property: 'text-shadow', values: `${shadows.join(', ')}` })
  //   return
  // }

  return {
    property: 'box-shadow',
    // It's reversed to match the Mac app order
    values: [...outerShadows.reverse(), ...innerShadows.reverse()],
  }
}

const getOpacityCSS = (style: CSSStyle): PropertyStyle | undefined => {
  const opacity = roundUpTo(style.appearance?.opacity ?? 1, 2)

  // Omit default value
  if (opacity === '1') return

  return {
    property: 'opacity',
    values: [{ value: opacity }],
  }
}

const gradientStops = (gradient: Gradient): InnerPropertyStyle[] => {
  const stops = gradient.stops
    .sort((lhs, rhs) => lhs.position - rhs.position)
    .map(stop => {
      return {
        color: formatCssRgbaColor(stop.color),
        value: `${roundWithLocale(stop.position * 100, 0)}%`,
      }
    })

  return stops
}

const gradientBackground = (gradient: Gradient): InnerPropertyStyle => {
  if (gradient.type === 'angular' || gradient.type === 'radial') {
    return {
      value: 'transparent',
    }
  }

  const angle = Math.round(gradientAngle(gradient))
  const stops = gradientStops(gradient)
  return {
    innerProperty: {
      property: 'linear-gradient',
      values: [
        {
          value: `${angle}deg`,
        },
        ...stops,
      ],
    },
  }
}

const getBackgroundCSS = (style: CSSStyle): PropertyStyle[] | undefined => {
  const css: PropertyStyle[] = []

  // ignore backgrounds if this is a text-style
  if (style.text) {
    return
  }

  const backgrounds: string[] = []
  const gradients: InnerPropertyStyle[] = []

  style.fills?.forEach(fill => {
    if (!fill.isEnabled) {
      return
    }

    if (fill.type === 'color' && fill.color) {
      const rgba = formatCssRgbaColor(fill.color)
      backgrounds.push(rgba)
    } else if (fill.type === 'gradient' && fill.gradient) {
      switch (fill.gradient.type) {
        case 'radial':
          gradients.push({
            innerProperty: {
              property: 'radial-gradient',
              values: gradientStops(fill.gradient),
            },
          })
          break
        case 'angular':
          gradients.push({
            innerProperty: {
              property: 'conic-gradient',
              values: [
                { value: 'from 90deg' },
                ...gradientStops(fill.gradient),
              ],
            },
          })
          break
        default:
          gradients.push(gradientBackground(fill.gradient))
      }
    } else {
      if (fill.patternTileScale) {
        css.push({
          property: 'background-size',
          values: [{ value: `${fill.patternTileScale * 100}%` }],
        })
      }
    }
  })

  if (backgrounds.length) {
    if (backgrounds.length > 1) {
      css.push({
        property: 'background',
        values: [
          {
            innerProperty: {
              property: backgrounds.length > 1 ? 'linear-gradient' : undefined,
              values: backgrounds.map(color => ({
                color,
              })),
            },
          },
        ],
      })
    } else {
      css.push({
        property: 'background-color',
        values: backgrounds.map(color => ({
          color,
        })),
      })
    }
  }

  if (gradients.length) {
    css.push({
      property: 'background',
      values: gradients,
    })
  }

  return css
}

const getTextCSS = (style: CSSStyle): PropertyStyle[] | undefined => {
  const css: PropertyStyle[] = []

  if (!isTextLayerStyle(style) || !style.text) {
    return
  }

  if (style.text.color) {
    const rgba = formatCssRgbaColor(style.text.color)

    css.push({ property: 'color', values: [{ color: rgba }] })
  }

  if (style.text.font) {
    const familyName = getComputedCssFontFamilyName(
      style.text.font.family,
      style.text.font.style
    )

    css.push({ property: 'font-family', values: [{ value: familyName }] })

    if (style.text.font.size) {
      css.push({
        property: 'font-size',
        values: [{ value: `${style.text.font.size}px` }],
      })
    }

    if (style.text.font.weight) {
      css.push({
        property: 'font-weight',
        values: [{ value: style.text.font.weight }],
      })
    }

    if (
      style.text.font.style &&
      // Omit default value
      style.text.font.style !== 'Regular'
    ) {
      css.push({
        property: 'font-style',
        values: [{ value: style.text.font.style.toLowerCase() }],
      })
    }
  }

  const roundedLetterSpacing = roundWithLocale(style.text.letterSpacing ?? 0, 2)
  // Omit default value
  if (roundedLetterSpacing !== '0') {
    css.push({
      property: 'letter-spacing',
      values: [{ value: `${roundedLetterSpacing}px` }],
    })
  }

  if (
    style.text.paragraphStyle?.alignment &&
    // Omit default value
    style.text.paragraphStyle?.alignment !== 'left'
  ) {
    css.push({
      property: 'text-align',
      values: [{ value: style.text.paragraphStyle.alignment }],
    })

    if (style.text.paragraphStyle.lineHeight) {
      css.push({
        property: 'line-height',
        values: [{ value: `${style.text.paragraphStyle.lineHeight}px` }],
      })
    }
  }

  if (style.text.transform) {
    css.push({
      property: 'text-transform',
      values: [{ value: style.text.transform }],
    })
  }

  if (style.text.decoration) {
    css.push({
      property: 'text-decoration',
      values: [{ value: style.text.decoration }],
    })
  }

  return css
}

const getSizeCSS = (
  element: SketchFrameElement | SketchLayerElement
): PropertyStyle[] => {
  return [
    {
      property: 'width',
      values: [{ value: roundAndFormatPixelValue(element.layerSize.width) }],
    },
    {
      property: 'height',
      values: [{ value: roundAndFormatPixelValue(element.layerSize.height) }],
    },
  ]
}

const getTransformCSS = (
  element: SketchLayerCSSProperties
): PropertyStyle | undefined => {
  if (!element.rotation) return

  const rotate = element.rotation
  const reversedRotate = rotate !== 0 ? -rotate : rotate
  const roundedRotate = `${roundWithLocale(reversedRotate, 2)}`

  return {
    property: 'transform',
    values: [
      {
        innerProperty: {
          property: 'rotate',
          values: [{ value: `${roundedRotate}deg` }],
        },
      },
    ],
  }
}

const getFilterCSS = (
  element: SketchLayerCSSProperties
): PropertyStyle | undefined => {
  if (!element.style) return

  if ('blur' in element.style && element.style.blur?.isEnabled) {
    return {
      property: 'filter',
      values: [
        {
          innerProperty: {
            property: 'blur',
            values: [{ value: `${element.style.blur.radius}px` }],
          },
        },
      ],
    }
  }
}

const getMainAxisAlignment = (value: string): string => {
  switch (value) {
    case MainAxisAlignment.Start:
      return 'flex-start'
    case MainAxisAlignment.Center:
      return 'center'
    case MainAxisAlignment.End:
      return 'flex-end'
    case MainAxisAlignment.SpaceBetween:
      return 'space-between'
    case MainAxisAlignment.SpaceAround:
      return 'space-around'
    case MainAxisAlignment.SpaceEvenly:
      return 'space-evenly'
    default:
      return 'flex-start'
  }
}

const getCrossAxisAlignment = (value: string): string => {
  switch (value) {
    case CrossAxisAlignment.Start:
      return 'flex-start'
    case CrossAxisAlignment.Center:
      return 'center'
    case CrossAxisAlignment.End:
      return 'flex-end'
    default:
      return 'flex-start'
  }
}

const getPadding = (padding: StackPadding): PropertyStyle => {
  const uniquePaddings = new Set([
    padding.top,
    padding.right,
    padding.bottom,
    padding.left,
  ])

  if (uniquePaddings.size === 1) {
    return {
      property: 'padding',
      values: [
        {
          value: roundAndFormatPixelValue(padding.top),
        },
      ],
    }
  } else if (padding.top === padding.bottom && uniquePaddings.size === 2) {
    return {
      property: 'padding',
      values: [
        {
          value: roundAndFormatPixelValue(padding.top),
        },
        {
          value: roundAndFormatPixelValue(padding.right),
        },
      ],
    }
  }

  return {
    property: 'padding',
    values: [
      {
        value: roundAndFormatPixelValue(padding.top),
      },
      {
        value: roundAndFormatPixelValue(padding.right),
      },
      {
        value: roundAndFormatPixelValue(padding.bottom),
      },
      {
        value: roundAndFormatPixelValue(padding.left),
      },
    ],
  }
}

const getStackCSS = (
  element: SketchLayerCSSProperties
): PropertyStyle[] | undefined => {
  if (!('stack' in element) || !element.stack) return

  const stack = element.stack

  const css: PropertyStyle[] = [
    {
      property: 'display',
      values: [
        {
          value: 'flex',
        },
      ],
    },
  ]

  const direction = stack?.direction === 'horizontal' ? 'row' : 'column'
  // Omit default value
  if (direction !== 'row') {
    css.push({
      property: 'flex-direction',
      values: [
        {
          value: direction,
        },
      ],
    })
  }

  // Omit default value
  if (stack?.mainAxisAlignment) {
    const justify = getMainAxisAlignment(stack?.mainAxisAlignment)
    if (justify !== 'flex-start') {
      css.push({
        property: 'justify-content',
        values: [
          {
            value: justify,
          },
        ],
      })
    }
  }

  // Omit default value
  if (stack?.crossAxisAlignment) {
    const align = getCrossAxisAlignment(stack?.crossAxisAlignment)
    if (align !== 'flex-start') {
      css.push({
        property: 'align-items',
        values: [
          {
            value: align,
          },
        ],
      })
    }
  }

  // Omit default value
  if (stack?.gap && stack.gap !== 0) {
    css.push({
      property: 'gap',
      values: [
        {
          value: roundAndFormatPixelValue(stack.gap),
        },
      ],
    })
  }

  if (stack?.padding) {
    const uniquePaddings = new Set([
      stack.padding.top,
      stack.padding.right,
      stack.padding.bottom,
      stack.padding.left,
    ])

    // Omit default value
    if (uniquePaddings.size === 1 && !uniquePaddings.has(0)) {
      css.push(getPadding(stack.padding))
    }
  }

  return css
}

export const useGenerateLayerCSS = (
  element?: SketchLayerCSSProperties
): GenerateLayerCSSValue => {
  const layerCSS: PropertyStyle[] = []

  // Hooks can't be conditional, so we need to handle this case here, that's
  // why style argument can be undefined
  if (!element || !element.style) return { css: layerCSS, toString: () => '' }

  const style = element?.style

  if (!layerCSS.length) {
    const stack = getStackCSS(element)
    if (stack) layerCSS.push(...stack)

    const sizeCSS = getSizeCSS(element)
    layerCSS.push(...sizeCSS)

    const borderCSS = getBorderCSS(style)
    const shadowsCSS = getShadowsCSS(style)
    if (borderCSS) {
      // Check if there are multiple borders, if so we need to merge them
      // with shadows
      if (borderCSS.values?.length && shadowsCSS.values.length) {
        layerCSS.push({
          property: 'box-shadow',
          values: [...borderCSS.values, ...shadowsCSS.values],
        })
      } else {
        layerCSS.push(...(Array.isArray(borderCSS) ? borderCSS : [borderCSS]))
      }
    }

    // We don't have border to be added as shadow, so we only need to add
    // shadows
    if (!borderCSS?.values?.length && shadowsCSS.values.length) {
      layerCSS.push(shadowsCSS)
    }

    const cornerCSS = getCornerCSS(element)
    if (cornerCSS) layerCSS.push(cornerCSS)

    const opacityCSS = getOpacityCSS(style)
    if (opacityCSS) layerCSS.push(opacityCSS)

    const backgroundCSS = getBackgroundCSS(style)
    if (backgroundCSS?.length) layerCSS.push(...backgroundCSS)

    const textCSS = getTextCSS(style)
    if (textCSS?.length) layerCSS.push(...textCSS)

    const transform = getTransformCSS(element)
    if (transform) layerCSS.push(transform)

    const filter = getFilterCSS(element)
    if (filter) layerCSS.push(filter)
  }

  const toString = (): string => {
    const formatValueAndColorString = (value?: string, color?: string) => {
      const valueStr = value?.trim() || ''
      const colorStr = color || ''

      return valueStr && color
        ? `${valueStr} ${color}`
        : `${valueStr}${colorStr}`
    }

    return layerCSS
      .map(property => {
        const propertyValues = property.values
          .map(value => {
            if (value.innerProperty) {
              const { property, values } = value.innerProperty
              const innerValuesStr = values
                .map(innerValue => {
                  return formatValueAndColorString(
                    innerValue.value,
                    innerValue.color
                  )
                })
                .join(', \n')

              return `${property}(${innerValuesStr})`
            } else {
              return formatValueAndColorString(value.value, value.color)
            }
          })
          .join(',\n')

        return `${property.property}: ${propertyValues};`
      })
      .join('\n')
  }

  return { css: layerCSS, toString }
}
